import { Controller } from "@hotwired/stimulus"

// Live global-search typeahead. Debounces the input, fetches /search.json,
// and renders a grouped dropdown of results directly under the box.
// Enter (with nothing highlighted) submits the full results page.
export default class extends Controller {
  static targets = ["input", "results"]

  connect() {
    this.debounceTimer = null
    this.activeIndex = -1
    this.onDocClick = this.handleOutsideClick.bind(this)
    document.addEventListener("click", this.onDocClick)
  }

  disconnect() {
    document.removeEventListener("click", this.onDocClick)
    clearTimeout(this.debounceTimer)
  }

  onInput() {
    clearTimeout(this.debounceTimer)
    const query = this.inputTarget.value.trim()
    if (query.length < 2) {
      this.close()
      return
    }
    this.debounceTimer = setTimeout(() => this.fetchResults(query), 200)
  }

  async fetchResults(query) {
    try {
      const response = await fetch(`/search.json?q=${encodeURIComponent(query)}`, {
        headers: { "Accept": "application/json" }
      })
      if (!response.ok) return
      const data = await response.json()
      this.render(data)
    } catch (_e) {
      // Network hiccup — leave the box quiet rather than erroring at the user.
    }
  }

  render(data) {
    const groups = data.groups || []
    if (groups.length === 0) {
      this.resultsTarget.innerHTML = `
        <div class="px-4 py-3 text-sm text-slate-500 dark:text-slate-400">
          No matches for “${this.escape(data.query)}”.
        </div>`
      this.open()
      return
    }

    this.activeIndex = -1
    let html = ""
    groups.forEach((group) => {
      html += `<div class="px-3 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">${this.escape(group.group)}</div>`
      group.hits.forEach((hit) => {
        html += `
          <a href="${hit.path}" data-search-hit
             class="flex items-center justify-between gap-3 px-4 py-2 text-sm hover:bg-brand-50 dark:hover:bg-slate-800 focus:bg-brand-50 dark:focus:bg-slate-800 focus:outline-none">
            <span class="truncate font-medium text-slate-800 dark:text-slate-100">${this.escape(hit.title)}</span>
            <span class="shrink-0 truncate text-xs text-slate-400 dark:text-slate-500">${this.escape(hit.subtitle || "")}</span>
          </a>`
      })
    })
    html += `
      <a href="/search?q=${encodeURIComponent(data.query)}"
         class="block border-t border-slate-100 dark:border-slate-800 px-4 py-2 text-center text-xs font-semibold text-brand-600 hover:bg-slate-50 dark:text-brand-400 dark:hover:bg-slate-800">
        View all results →
      </a>`
    this.resultsTarget.innerHTML = html
    this.open()
  }

  onKeydown(event) {
    if (!this.isOpen) return
    const hits = this.hitElements()
    if (event.key === "ArrowDown") {
      event.preventDefault()
      this.activeIndex = Math.min(this.activeIndex + 1, hits.length - 1)
      this.highlight(hits)
    } else if (event.key === "ArrowUp") {
      event.preventDefault()
      this.activeIndex = Math.max(this.activeIndex - 1, -1)
      this.highlight(hits)
    } else if (event.key === "Enter") {
      if (this.activeIndex >= 0 && hits[this.activeIndex]) {
        event.preventDefault()
        window.location.href = hits[this.activeIndex].getAttribute("href")
      }
    } else if (event.key === "Escape") {
      this.close()
    }
  }

  highlight(hits) {
    hits.forEach((el, i) => el.classList.toggle("bg-brand-50", i === this.activeIndex))
    if (this.activeIndex >= 0 && hits[this.activeIndex]) {
      hits[this.activeIndex].scrollIntoView({ block: "nearest" })
    }
  }

  hitElements() {
    return Array.from(this.resultsTarget.querySelectorAll("[data-search-hit]"))
  }

  open() {
    this.resultsTarget.classList.remove("hidden")
    this.isOpen = true
  }

  close() {
    this.resultsTarget.classList.add("hidden")
    this.resultsTarget.innerHTML = ""
    this.isOpen = false
    this.activeIndex = -1
  }

  handleOutsideClick(event) {
    if (!this.element.contains(event.target)) this.close()
  }

  escape(str) {
    const div = document.createElement("div")
    div.textContent = str == null ? "" : String(str)
    return div.innerHTML
  }
}
